import type {
  ChannelProvider,
  CrossTabMessage,
  LockProvider
} from './types';

const MASTER_LOCK_NAME = 'sq-master';
const CHANNEL_NAME = 'sq-broadcast';

/**
 * Real Web Locks provider. Falls back to "assume master" if unavailable
 * (older browsers, jsdom without polyfill).
 *
 * The `signal` is used to cancel pending requests when a store instance is
 * destroyed (e.g., HMR reloading App.svelte). Without it, stale instances
 * pile up in the Web Locks queue and can steal the lock from the live one.
 */
export function createBrowserLockProvider(): LockProvider {
  return {
    request(
      name: string,
      callback: () => Promise<void>,
      signal?: AbortSignal
    ): void {
      if (
        typeof navigator === 'undefined' ||
        typeof navigator.locks === 'undefined' ||
        typeof navigator.locks.request !== 'function'
      ) {
        if (signal?.aborted) return;
        void callback();
        return;
      }
      void navigator.locks
        .request(name, { signal }, () => callback())
        .catch((err: unknown) => {
          const name = (err as { name?: string } | null)?.name;
          if (name !== 'AbortError') {
            console.error('[StudyQuantifier] cross-tab lock failed:', err);
          }
        });
    }
  };
}

/**
 * Real BroadcastChannel provider. Falls back to a no-op if unavailable.
 */
export function createBrowserChannelProvider(): ChannelProvider {
  if (typeof BroadcastChannel === 'undefined') {
    return {
      postMessage: () => undefined,
      onMessage: () => () => undefined,
      close: () => undefined
    };
  }

  const channel = new BroadcastChannel(CHANNEL_NAME);

  return {
    postMessage(message: CrossTabMessage): void {
      try {
        channel.postMessage(message);
      } catch {
        // structured-clone failure: swallow, this is best-effort cross-tab
      }
    },
    onMessage(handler: (message: CrossTabMessage) => void): () => void {
      const listener = (ev: MessageEvent) => {
        handler(ev.data as CrossTabMessage);
      };
      channel.addEventListener('message', listener);
      return () => {
        channel.removeEventListener('message', listener);
      };
    },
    close(): void {
      channel.close();
    }
  };
}

export const MASTER_LOCK = MASTER_LOCK_NAME;
export const BROADCAST_CHANNEL = CHANNEL_NAME;